// src/pages/Level-7.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import LevelTemplate from "./LevelTemplate";
import { STREAM_VIDEOS } from "../videos/streamVideos";
import { TrophyStorage } from "../services/trophyStorage";

/* ─────────────────────────────────────────────
   CONFIG – Level 7 (V1.1) — London Bridge
   - Scene video first, then melody page
   - Cloudflare Stream
   - Uses your Level 7 hero image
   ───────────────────────────────────────────── */

// Cloudflare Stream Video ID (from your screenshot)
const LEVEL7_FALLBACK_STREAM_ID = "e5d66b9df96f27e3363d5a871c00cf6b";

const LEVEL7_STREAM_ID =
  STREAM_VIDEOS?.L7_LONDON_INTRO?.id || LEVEL7_FALLBACK_STREAM_ID;

// ✅ LevelTemplate supports: videoSrc="stream:<id>"
const LEVEL7_VIDEO_SRC = `stream:${LEVEL7_STREAM_ID}`;

// ✅ Hero image (place file in: public/images/LondonBridge-Hero.png)
const LEVEL7_HERO_IMAGE =
  "/images/" + encodeURIComponent("LondonBridge-Hero.png");

/* ─────────────────────────────────────────────
   MELODY — London Bridge
   Notes + syllable groupings matched to your sheet.
   ───────────────────────────────────────────── */

const LEVEL7_MELODY_LINES = [
  {
    id: 0,
    notes: ["G", "A", "G", "F", "E", "F", "G"],
    lyric: "London Bridge is falling down,",
  },
  {
    id: 1,
    notes: ["D", "E", "F", "E", "F", "G"],
    lyric: "Falling down, falling down.",
  },
  {
    id: 2,
    notes: ["G", "A", "G", "F", "E", "F", "G"],
    lyric: "London Bridge is falling down,",
  },
  { id: 3, notes: ["D", "G", "E", "C"], lyric: "My fair lady!" },
  {
    id: 4,
    notes: ["G", "A", "G", "F", "E", "F", "G"],
    lyric: "Build it up with iron bars,",
  },
  {
    id: 5,
    notes: ["D", "E", "F", "E", "F", "G"],
    lyric: "Iron bars, iron bars.",
  },
  {
    id: 6,
    notes: ["G", "A", "G", "F", "E", "F", "G"],
    lyric: "Build it up with iron bars,",
  },
  { id: 7, notes: ["D", "G", "E", "C"], lyric: "My fair lady!" },
];

const LEVEL7_LYRIC_GROUPS = [
  ["Lon-don", "Bridge", "is", "fall-ing", "down,"],
  ["Fall-ing", "down,", "fall-ing", "down."],
  ["Lon-don", "Bridge", "is", "fall-ing", "down,"],
  ["My", "fair", "la-", "dy!"],
  ["Build", "it", "up", "with", "i-ron", "bars,"],
  ["I-ron", "bars,", "i-ron", "bars."],
  ["Build", "it", "up", "with", "i-ron", "bars,"],
  ["My", "fair", "la-", "dy!"],
];

const LEVEL7_NOTE_GROUP_INDEXES = [
  [
    { start: 0, end: 1 }, // Lon-don
    { start: 2, end: 2 }, // Bridge
    { start: 3, end: 3 }, // is
    { start: 4, end: 5 }, // fall-ing
    { start: 6, end: 6 }, // down,
  ],
  [
    { start: 0, end: 1 }, // Fall-ing
    { start: 2, end: 2 }, // down,
    { start: 3, end: 4 }, // fall-ing
    { start: 5, end: 5 }, // down.
  ],
  [
    { start: 0, end: 1 }, // Lon-don
    { start: 2, end: 2 }, // Bridge
    { start: 3, end: 3 }, // is
    { start: 4, end: 5 }, // fall-ing
    { start: 6, end: 6 }, // down,
  ],
  [
    { start: 0, end: 0 }, // My
    { start: 1, end: 1 }, // fair
    { start: 2, end: 2 }, // la-
    { start: 3, end: 3 }, // dy!
  ],
  [
    { start: 0, end: 0 }, // Build
    { start: 1, end: 1 }, // it
    { start: 2, end: 2 }, // up
    { start: 3, end: 3 }, // with
    { start: 4, end: 5 }, // i-ron
    { start: 6, end: 6 }, // bars,
  ],
  [
    { start: 0, end: 1 }, // I-ron
    { start: 2, end: 2 }, // bars,
    { start: 3, end: 4 }, // i-ron
    { start: 5, end: 5 }, // bars.
  ],
  [
    { start: 0, end: 0 }, // Build
    { start: 1, end: 1 }, // it
    { start: 2, end: 2 }, // up
    { start: 3, end: 3 }, // with
    { start: 4, end: 5 }, // i-ron
    { start: 6, end: 6 }, // bars,
  ],
  [
    { start: 0, end: 0 }, // My
    { start: 1, end: 1 }, // fair
    { start: 2, end: 2 }, // la-
    { start: 3, end: 3 }, // dy!
  ],
];

/* ─────────────────────────────────────────────
   HERO (NO overlay text)
   + Emoji burst:
     - correct: 🐼
     - wrong: 💨
   ───────────────────────────────────────────── */

function Level7Hero({ backgroundImage, noteEvent }) {
  const [bursts, setBursts] = useState([]);
  const idRef = useRef(1);
  const lastEventRef = useRef(null);

  const safeRand = useMemo(() => {
    const seed = Math.random() * 9999;
    return () => {
      const x = Math.sin(seed + idRef.current * 12.9898) * 43758.5453;
      return x - Math.floor(x);
    };
  }, []);

  useEffect(() => {
    if (!noteEvent) return;
    if (noteEvent === lastEventRef.current) return;

    lastEventRef.current = noteEvent;

    const isHit = noteEvent === "hit";
    const emoji = isHit ? "🐼" : "💨"; // ✅ Panda for correct notes

    const id = idRef.current++;
    const left = 18 + safeRand() * 64;
    const top = 18 + safeRand() * 52;

    const burst = { id, emoji, left, top, isHit };
    setBursts((prev) => [...prev, burst]);

    const t = setTimeout(() => {
      setBursts((prev) => prev.filter((b) => b.id !== id));
    }, 750);

    return () => clearTimeout(t);
  }, [noteEvent, safeRand]);

  return (
    <div className="relative w-full max-w-3xl mx-auto rounded-[22px] sm:rounded-[24px] shadow bg-slate-900/5 overflow-hidden">
      {backgroundImage && (
        <img
          src={backgroundImage}
          alt=""
          className="w-full h-40 sm:h-56 object-cover"
          style={{ objectPosition: "50% 45%" }}
          draggable={false}
        />
      )}

      {bursts.map((b) => (
        <div
          key={b.id}
          className="tt-burst"
          style={{ left: `${b.left}%`, top: `${b.top}%` }}
          aria-hidden="true"
        >
          <span
            className={
              "tt-burst-emoji " + (b.isHit ? "tt-burst-hit" : "tt-burst-miss")
            }
          >
            {b.emoji}
          </span>
        </div>
      ))}

      <style>{`
        .tt-burst{
          position:absolute;
          transform: translate(-50%, -50%);
          pointer-events:none;
          z-index: 5;
        }
        .tt-burst-emoji{
          display:inline-flex;
          align-items:center;
          justify-content:center;
          font-size: 34px;
          filter: drop-shadow(0 6px 10px rgba(0,0,0,0.25));
          animation: ttFloatUp 750ms cubic-bezier(.2,.9,.2,1) forwards;
          will-change: transform, opacity;
        }
        @keyframes ttFloatUp{
          0%   { transform: translateY(0px) scale(1); opacity: 1; }
          70%  { transform: translateY(-26px) scale(1.18); opacity: 1; }
          100% { transform: translateY(-46px) scale(1.05); opacity: 0; }
        }
      `}</style>
    </div>
  );
}

export default function Level7() {
  const navigate = useNavigate();

  return (
    <LevelTemplate
      levelId={7}
      currentLevelForSidebar={7}
      videoSrc={LEVEL7_VIDEO_SRC}
      startLabel="Start Game"
      melodyLines={LEVEL7_MELODY_LINES}
      lyricGroupsPerLine={LEVEL7_LYRIC_GROUPS}
      noteGroupIndexesPerLine={LEVEL7_NOTE_GROUP_INDEXES}
      backgroundImage={LEVEL7_HERO_IMAGE}
      heroImageFit="cover"
      renderHero={(heroProps) => (
        <Level7Hero
          backgroundImage={heroProps.backgroundImage}
          noteEvent={heroProps.noteEvent}
        />
      )}
      completionTitle="Panda Star unlocked!"
      completionBody={
        <>
          You played{" "}
          <span className="font-semibold text-[#4F46E5]">London Bridge</span>{" "}
          perfectly 🌉🎶 Now the bridge is strong again — collect your{" "}
          <span className="font-semibold text-[#4F46E5]">Panda Star</span> and
          move forward!
        </>
      }
      // ✅ SUCCESS TRIGGER (Level-7 completed + star "L7" collected)
      onComplete={() => {
        TrophyStorage.markLevelAndStar(7); // marks level "7" + star "L7"
      }}
      onNextLevel={() => navigate("/level-8")}
    />
  );
}
